import React, { useEffect, useMemo, useState } from 'react';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Table,
  Button,
  Badge,
  UncontrolledTooltip,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import moment from 'moment';
import AccountGroup from 'mdi-react/CashMultipleIcon';
import PaymentIcon from 'mdi-react/PaymentIcon';
import CashOnDeliveryIcon from 'mdi-react/TruckDeliveryIcon';
import { Parser } from 'json2csv';
import fileDownload from 'js-file-download';
import CheckIcon from '../../../shared/components/custom/CheckIcon';
import { formatCurrencyNumber } from '../../../util/functions';
import t, { partial } from '../../../util/translation/translation';
import { formatDateTime } from '../../../util/date-and-time';
import useConfig from '../../../util/useConfig';
import Api from '../../../util/api';
import ValueDisplayCounter from './ValueDisplayCounter';
import Loading from '../../../shared/components/custom/Loading';

const AuctionObjectList = ({
  auctionHouseId,
  handleModal,
  setLoading,
  deleteVisible,
}) => {
  const config = useConfig();
  const p = partial('auctionObjectsShared');
  const s = partial('shared');
  const [auctionItems, setAuctionItems] = useState([]);
  const totalBidValue = useMemo(
    () =>
      auctionItems.reduce((tot, curr) => {
        return tot + curr.lastBid || 0;
      }, 0),
    [auctionItems]
  );
  const totalBidPaid = useMemo(
    () =>
      auctionItems.reduce((tot, curr) => {
        return tot + (curr?.bids[0]?.transactionId ? curr.lastBid : 0) || 0;
      }, 0),
    [auctionItems]
  );

  const totalDeliveryPaid = useMemo(
    () =>
      auctionItems.reduce((tot, curr) => {
        return (
          tot +
            (curr?.bids[0]?.deliveryDetails?.deliveryType === 'DELIVERY'
              ? curr.deliveryPrice
              : 0) || 0
        );
      }, 0),
    [auctionItems]
  );
  const getAuctionHouseItems = async () => {
    setLoading(true);
    try {
      const resAuctionItems =
        await Api.auctions.getAuctionItemsForHouse(auctionHouseId);
      setAuctionItems(resAuctionItems);
      setLoading(false);
      const auctionItemIds = resAuctionItems.map((item) => item.id);
      const { data: pressData } = await Api.auctions.getAuctionsPressAmount({
        auctionItemIds,
      });
      const mapped = resAuctionItems.map((item) => ({
        ...item,
        views:
          pressData.find((itemPress) => itemPress.itemId === item.id)
            ?.presses || 0,
      }));
      setAuctionItems(mapped);
    } catch (err) {
      toast.error(JSON.stringify(err));
      setLoading(false);
    }
  };
  useEffect(() => {
    if (auctionHouseId) {
      getAuctionHouseItems();
    }
  }, [auctionHouseId]);
  useEffect(() => {
    if (!deleteVisible && auctionHouseId) {
      getAuctionHouseItems();
    }
  }, [deleteVisible, auctionHouseId]);

  const renderAuctionObjectList = () => {
    console.log(auctionItems);
    return auctionItems.map((auctionItem, i) => (
      <tr
        style={{ background: i % 2 ? '#fff' : '#F9FAFB' }}
        key={`auctionItem-${auctionItem.id}`}
      >
        <td>
          <CheckIcon
            state={
              auctionItem.isActive &&
              moment().isBetween(
                moment(auctionItem.startDate),
                moment(auctionItem.endDate)
              )
            }
          />
        </td>
        <td>
          <Link to={`/auctions/edit/${auctionHouseId}/edit/${auctionItem.id}`}>
            {auctionItem.name}
          </Link>
        </td>
        <td>{`${formatDateTime(auctionItem.startDate)} - ${formatDateTime(auctionItem.endDate)}`}</td>
        <td>
          {auctionItem.views !== undefined ? (
            auctionItem.views
          ) : (
            <Loading loading size={40} />
          )}
        </td>
        <td>{auctionItem.totalBids}</td>
        <td>
          <b>
            {formatCurrencyNumber(auctionItem.lastBid)} {config.currencyCaps}
          </b>
        </td>
        <td>
          {auctionItem?.bids[0]?.transactionId && (
            <Badge color={'success'}>{t('transactions.status-paid')}</Badge>
          )}
        </td>
        <td>
          <UncontrolledDropdown className="dashboard__table-more">
            <DropdownToggle>
              <DotsHorizontalIcon />
            </DropdownToggle>
            <DropdownMenu className="dropdown__menu">
              <Link
                to={`/auctions/edit/${auctionHouseId}/edit/${auctionItem.id}`}
              >
                <DropdownItem>{s('edit')}</DropdownItem>
              </Link>
              <Link
                to={`/auctions/edit/${auctionHouseId}/clone/${auctionItem.id}`}
              >
                <DropdownItem>{s('clone')}</DropdownItem>
              </Link>
              <DropdownItem
                onClick={() => handleModal(auctionItem.id)}
                className="danger"
              >
                {s('deleteCaps')}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </td>
      </tr>
    ));
  };
  const handleCsvExport = async () => {
    try {
      // for all items gets bids + users
      setLoading(true);

      const auctionItemsWithBids = await Promise.all(
        auctionItems.map((item) =>
          Api.auctions
            .getAuctionItemBids(item.id, { page: 1, take: 1 })
            .then(({ data }) => ({ ...item, bids: data }))
        )
      );
      const userIds = [
        ...new Set(
          auctionItemsWithBids.flatMap((item) =>
            item.bids.map((bid) => bid.userId)
          )
        ),
      ];
      const { data: usersByIds } = await Api.users.getUsersByCloudIds(userIds);
      const userByIdMap = {};
      for (let i = 0; i < usersByIds.length; i++) {
        userByIdMap[usersByIds[i].userCloudId] = usersByIds[i];
      }
      const mappedItems = auctionItems.map((item) => ({
        name: item.name,
        description: item.description,
        startDate: moment(item.startDate).format('DD/MM/YYYY HH:mm'),
        endDate: moment(item.endDate).format('DD/MM/YYYY HH:mm'),
        highestBid: item.bids[0]?.amount || 'empty',
        deliveryPrice:
          item.bids[0]?.deliveryDetails?.deliveryType !== 'PICK_UP'
            ? item.deliveryPrice
            : 0,
        isPaid: item.bids[0]?.transactionId ? s('yes') : s('no'),
        phoneNumber: item.bids[0]?.userId
          ? userByIdMap[item.bids[0]?.userId]?.phoneNumber
          : 'empty',
        email: item.bids[0]?.userId
          ? userByIdMap[item.bids[0]?.userId]?.email
          : 'empty',
        firstName: item.bids[0]?.userId
          ? userByIdMap[item.bids[0]?.userId]?.firstname
          : 'empty',
        lastName: item.bids[0]?.userId
          ? userByIdMap[item.bids[0]?.userId]?.lastname
          : 'empty',
        pickupStatus:
          item.bids[0]?.deliveryDetails?.deliveryType === 'PICK_UP'
            ? p('pickup')
            : p('delivery'),
        deliveryAddress:
          item.bids[0]?.deliveryDetails?.deliveryType === 'PICK_UP'
            ? s('notApplicable')
            : `${item.bids[0]?.deliveryDetails?.address || ''}, ${item.bids[0]?.deliveryDetails?.city || ''}, ${item.bids[0]?.deliveryDetails?.zip || ''}`
                .replace(/^,\s*|,\s*$/g, '')
                .replace(/,\s*,/g, ','),
        deliveryStatus:
          item.bids[0]?.deliveryDetails?.deliveryStatus === 'SENT'
            ? p('deliveryStatusSent')
            : p('deliveryStatusNotSent'),
      }));

      const fields = [
        { label: s('name'), value: 'name' },
        { label: s('description'), value: 'description' },
        { label: s('startDate'), value: 'startDate' },
        { label: s('endDate'), value: 'endDate' },
        { label: p('highestBid'), value: 'highestBid' },
        { label: s('paid'), value: 'isPaid' },
        { label: s('phone'), value: 'phoneNumber' },
        { label: s('email'), value: 'email' },
        { label: s('firstname'), value: 'firstName' },
        { label: s('lastname'), value: 'lastName' },
        { label: p('deliveryType'), value: 'pickupStatus' },
        { label: p('deliveryPrice'), value: 'deliveryPrice' },

        { label: s('address'), value: 'deliveryAddress' },
        { label: s('status'), value: 'deliveryStatus' },
      ];
      const json2csvParser = new Parser({ fields });
      const csv = json2csvParser.parse(mappedItems);
      fileDownload(`\uFEFF${csv}`, 'export.csv');
    } catch (err) {
      toast.error(err || `${p('listExportingERROR')}'!'`);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <p className="bold-text flex alignItems-center flex-HSpaceBetween">
        <div>
          <UncontrolledTooltip placement="bottom" target="addedValue">
            {p('auctionValue')}
          </UncontrolledTooltip>
          <UncontrolledTooltip placement="bottom" target="addedValue1">
            {p('auctionValuePaid')}
          </UncontrolledTooltip>
          <UncontrolledTooltip placement="bottom" target="addedValue2">
            {p('auctionDeliveryPaid')}
          </UncontrolledTooltip>
          <ValueDisplayCounter
            id="addedValue"
            color={'primary'}
            icon={
              <AccountGroup style={{ width: 24, display: 'inline-block' }} />
            }
            text={`${formatCurrencyNumber(totalBidValue)} ${config.currencyCaps}`}
          />
          <ValueDisplayCounter
            id="addedValue1"
            icon={
              <PaymentIcon style={{ width: 24, display: 'inline-block' }} />
            }
            text={`${formatCurrencyNumber(totalBidPaid + totalDeliveryPaid)} ${config.currencyCaps}`}
          />
          <ValueDisplayCounter
            id="addedValue2"
            icon={
              <CashOnDeliveryIcon
                style={{ width: 24, display: 'inline-block' }}
              />
            }
            text={`${formatCurrencyNumber(totalDeliveryPaid)} ${config.currencyCaps}`}
          />
        </div>
        <div>
          <Button
            className="margin-0"
            outline
            color="secondary"
            size="sm"
            onClick={handleCsvExport}
          >
            {p('exportWinningBid')}
          </Button>
          <Link
            style={{ marginLeft: 10 }}
            to={`/auctions/edit/${auctionHouseId}/create`}
          >
            <Button
              className="margin-0"
              color="primary"
              style={{ float: 'right' }}
              size="sm"
            >
              {p('createAuctionObject')}
            </Button>
          </Link>
        </div>
      </p>
      <div
        className="mt-4"
        style={{
          display: 'flex',
          paddingBottom: 5,
          marginTop: 5,
          background: '#fff',
          borderRadius: 6,
          border: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <Table responsive striped>
          <thead>
            <tr>
              <th className="bold" style={{ color: '#4D576A' }}>
                {s('active')}
              </th>
              <th className="bold" style={{ color: '#4D576A' }}>
                {s('product')}
              </th>
              <th className="bold" style={{ color: '#4D576A' }}>
                {s('date')}
              </th>
              <th className="bold" style={{ color: '#4D576A' }}>
                {s('views')}
              </th>
              <th className="bold" style={{ color: '#4D576A' }}>
                {p('totalBids')}
              </th>
              <th className="bold" style={{ color: '#4D576A' }}>
                {p('highestBid')}
              </th>
              <th className="bold" style={{ color: '#4D576A' }}>
                {s('paid')}
              </th>
              <th />
            </tr>
          </thead>
          <tbody>{renderAuctionObjectList()}</tbody>
        </Table>
      </div>
    </>
  );
};

export default AuctionObjectList;
